import { Component, Input } from "@angular/core";
import { DateTime } from "luxon";
import { DeliveryStates } from "src/core/interfaces/deliveryStates";
import { HourType } from "src/core/models/hourType";
import { HourTypeService } from "src/core/services/hourType.service";
import { DeliveryDto } from "../../../core/dto/delivery.dto";
import { TimeDto } from "../../../core/dto/time.dto";
import { DeliveryService } from "../../../core/services/delivery.service";
import { TimesService } from "../../../core/services/times.service";
import { UserService } from "../../../core/services/user.service";
import { LoadingHelper } from "../../../gyzmo-commons/helpers/loading.helper";
import { NavControllerExtended } from "../../../gyzmo-commons/helpers/navControllerExtended";
import { DATE_FORMAT_FOR_PICKER, HOUR_FORMAT } from "../../../gyzmo-commons/interfaces/constants";
import { DateProvider } from "../../../gyzmo-commons/interfaces/dateProvider";

@Component({
    selector: "popover-delivery-state",
    templateUrl: "delivery-state.popover.html",
    styleUrls: ["delivery-state.popover.scss"],
})
export class DeliveryStatePopover {
    @Input() delivery: DeliveryDto;
    @Input() action: string;

    DeliveryStates = DeliveryStates;
    startTime: string;
    lunchs: HourType[];
    selectedLunch: HourType = new HourType();

    constructor(private navCtrl: NavControllerExtended,
                private dateProvider: DateProvider,
                private loadingHelper: LoadingHelper,
                private deliveryService: DeliveryService,
                private userService: UserService,
                private timesService: TimesService,
                private hourTypeService: HourTypeService) {
        this.delivery = navCtrl.params.getParam("delivery");
        this.action = navCtrl.params.getParam("action");

        this.startTime = this.dateProvider.now().toFormat(HOUR_FORMAT);
    }

    ionViewDidEnter() {
        this.hourTypeService.getDbList("REPAS")
            .then(hourtypes => {
                this.lunchs = hourtypes;

                const noLunch = new HourType();
                noLunch.id = "AUCUN";
                noLunch.wording = "Aucun";
                this.lunchs.push(noLunch);
            });
    }

    public async dismiss(save: boolean) {
        if (save) {
            switch (this.action) {
                case "start":
                    await this.loadingHelper.showLoading();
                    this.delivery.startDate = DateTime.fromFormat(
                        this.dateProvider.now().toFormat(DATE_FORMAT_FOR_PICKER) + " " + this.startTime,
                        DATE_FORMAT_FOR_PICKER + " " + HOUR_FORMAT);
                    this.delivery.movement.startDate = DateTime.fromFormat(
                        this.dateProvider.now().toFormat(DATE_FORMAT_FOR_PICKER) + " " + this.startTime,
                        DATE_FORMAT_FOR_PICKER + " " + HOUR_FORMAT);

                    await this.deliveryService.updateMovement(this.delivery);

                    await this.changeDeliveryState(DeliveryStates.STARTED);
                    await this.loadingHelper.hideAll();
                    break;
                case "end":
                    await this.loadingHelper.showLoading();
                    this.delivery.returnDate = DateTime.fromFormat(
                        this.dateProvider.now().toFormat(DATE_FORMAT_FOR_PICKER) + " " + this.startTime,
                        DATE_FORMAT_FOR_PICKER + " " + HOUR_FORMAT);
                    this.delivery.movement.returnDate = DateTime.fromFormat(
                        this.dateProvider.now().toFormat(DATE_FORMAT_FOR_PICKER) + " " + this.startTime,
                        DATE_FORMAT_FOR_PICKER + " " + HOUR_FORMAT);

                    this.delivery.movement.isClosed = true;
                    await this.deliveryService.updateMovement(this.delivery);

                    let user = await this.userService.getCurrentUserWithThirdPartyOnly();

                    if (this.selectedLunch.id.toLowerCase() != "aucun") {
                        await this.deliveryService.recordLunch(user, this.delivery, this.selectedLunch.id);
                    }

                    // Delivery time
                    let time = new TimeDto();
                    time.fatherMovementId = this.delivery.id;
                    time.type = DeliveryStates.ENDED;
                    time.startTime = this.delivery.startDate;
                    time.endTime = this.delivery.returnDate;

                    await this.deliveryService.recordTimeForDelivery(this.delivery, time);

                    // Pause times
                    let times = await this.timesService.getTimesForDelivery(this.delivery.id, "", "", 0);
                    let filteredList = times.filter(element => {
                        return element.type == DeliveryStates.PAUSED;
                    });
                    for (const value of filteredList) {
                        await this.deliveryService.recordTimeForDelivery(this.delivery, value);
                    }

                    await this.changeDeliveryState(DeliveryStates.ENDED);
                    await this.loadingHelper.hideAll();
                    break;
            }
        }

        await this.navCtrl.navigateBack("/delivery-details", { delivery: this.delivery });
    }

    isValidationEnabled() {
        if (!this.delivery) {
            return false;
        }

        if (this.delivery.state == DeliveryStates.NOT_STARTED) {
            return this.startTime
                   && this.delivery.movement.startMileage
                   && this.delivery.movement.startMileage >= this.delivery.equipment.mileage;
        } else {
            return this.startTime
                   && this.selectedLunch
                   && this.delivery.movement.returnMileage
                   && this.delivery.movement.returnMileage > this.delivery.movement.startMileage;
        }
    }

    public onStartTimeChanged($event: CustomEvent) {
        this.startTime = $event.detail.value;
    }

    async changeDeliveryState(deliveryState: DeliveryStates) {
        this.delivery = await this.deliveryService.changeState(this.delivery, deliveryState);
    }
}

